import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Lang } from "@shared/types";
import en from "./en";
import ka, { type Dict } from "./ka";
import ru from "./ru";

export const DICTS: Record<Lang, Dict> = { ka, en, ru };
export const UI_LANGS: Lang[] = ["ka", "en", "ru"];
const KEY = "hg:lang";

/** Replace {name} placeholders. Unknown placeholders are left as-is. */
export function fmt(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

/** Pick a random string from a list (used for host lines and shouts). */
export function pickOne<T>(list: readonly T[], seed?: number): T {
  if (!list.length) throw new Error("empty list");
  const i = seed === undefined ? Math.floor(Math.random() * list.length) : Math.abs(seed) % list.length;
  return list[i];
}

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY) as Lang | null;
    if (saved && UI_LANGS.includes(saved)) return saved;
  } catch {
    /* storage blocked */
  }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "ka").slice(0, 2);
  if (nav === "ru") return "ru";
  if (nav === "en") return "ka"; // Georgian is the default for everyone unless they pick otherwise
  return "ka";
}

interface I18n {
  lang: Lang;
  d: Dict;
  setLang(l: Lang): void;
  f: typeof fmt;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = DICTS[lang].meta.title;
  }, [lang]);
  const value = useMemo<I18n>(() => ({ lang, d: DICTS[lang], setLang, f: fmt }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n outside provider");
  return v;
}

/** Dictionary for a specific language (e.g. the room's question language). */
export function dictFor(lang: Lang): Dict {
  return DICTS[lang] ?? ka;
}

export type { Dict };
